import { ref, defineComponent, computed, reactive, PropType } from '@vue/composition-api'
import {
  AddSubscriptionRequest,
  addCompanySubscription,
  SubscriptionCapability,
  SubscriptionPeriod,
  InvoiceCreationPeriod,
} from '@/functions/subscription-functions';
import {
  PaymentTypes,
  PromotionType,
  allPromotions,
  getDefaultPackagePrices,
} from '@/states/promotion-state'
import moment from 'moment'
import { Company } from '@/views/admin-companies/admin-companies-types';
import { getPromotionTitle } from '@/utilities/promotion-utilities';
import { i18n } from '@/utilities/vue-i18n';

export default defineComponent({
  props: {
    companyId: {
      type: Number,
      required: true
    },
    company: {
      type: Object as PropType<Company>,
      required: false
    }
  },
  setup(props, context) {
    const form = reactive({
      name: '',
      price: 0,
      capabilities: [] as SubscriptionCapability[],
      periodType: 'InfiniteSubscriptionPeriod' as 'TimeLimitedSubscriptionPeriod' | 'InfiniteSubscriptionPeriod',
      startMin: moment(new Date()).format('YYYY-MM'),
      endMin: moment(new Date()).format('YYYY-MM'),
      endDate: moment(new Date()).format('YYYY-MM'),
      startDate: moment(new Date()).format('YYYY-MM'),
      invoiceCreationPeriod: InvoiceCreationPeriod.none,
      enableCostCenterInput: false,
    });
    
    // Track capability selection state
    const selectingCapabilityType = ref(true)
    const selectedCapabilityType = ref<SubscriptionCapability['type'] | null>(null)
    const hasAddedFirstCapability = ref(false)
    
    const currentCapability = ref<SubscriptionCapability>({
      type: 'MonthlyCreditsCapability',
      description: '',
      credits: 0,
      promotionType: 'volume'
    })
    
    const submitting = ref(false)

    const isCapabilityValid = (capability: SubscriptionCapability) => {
      if (capability.type === 'MonthlyCreditsCapability') {
        return capability.credits > 0;
      } else if (capability.type === 'PriceChangeCapability') {
        // Check that at least one price is set
        const monetaryPrices = Object.values(capability.packagePrices.monetary);
        const creditPrices = Object.values(capability.packagePrices.credit);
        
        return monetaryPrices.some(price => price > 0) || 
               creditPrices.some(price => price > 0);
      }
      return false;
    }

    // Computed property to check if current capability being configured is valid
    const isCurrentCapabilityValid = computed(() => isCapabilityValid(currentCapability.value));

    // Computed property to check if form is valid
    const isFormValid = computed(() => {
      return form.name.trim() !== '' &&  
             form.capabilities.length > 0 &&
             (form.periodType !== 'TimeLimitedSubscriptionPeriod' || form.endDate) &&
             (form.periodType !== 'TimeLimitedSubscriptionPeriod' || form.startDate) &&
             (form.periodType !== 'TimeLimitedSubscriptionPeriod' || new Date(form.startDate) <= new Date(form.endDate)) &&
             (isCapabilityValid(form.capabilities[0]));
    })
    
    const formatPrice = (price: number) => {
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK'
      }).format(price)
    }

    const close = () => {
      context.emit('close')
    }

    const changeCapabilityType = (type: SubscriptionCapability['type']) => {
      switch(type) {
        case 'MonthlyCreditsCapability':
          currentCapability.value = {
            type: 'MonthlyCreditsCapability',
            description: currentCapability.value.description,
            credits: 0,
            promotionType: 'volume'
          }
          break;
        case 'PriceChangeCapability': 
          
          // Initialize package prices structure
          currentCapability.value = {
            type: 'PriceChangeCapability',
            description: currentCapability.value.description,
            packagePrices: {
              monetary: allPromotions
              .reduce((promoAcc, promo) => ({ ...promoAcc, [promo]: getDefaultPackagePrices(promo).price }), {} as Record<Exclude<PromotionType, 'none'>, number>),
              credit: allPromotions
              .reduce((promoAcc, promo) => ({ ...promoAcc, [promo]: getDefaultPackagePrices(promo).tokenPrice }), {} as Record<Exclude<PromotionType, 'none'>, number>),
            }
          }
          break;
      }
    };

    const selectCapabilityType = (type: SubscriptionCapability['type']) => {
      selectedCapabilityType.value = type;
      selectingCapabilityType.value = false;
      changeCapabilityType(type);
    }

    const cancelCapabilitySelection = () => {
      selectedCapabilityType.value = null;
      selectingCapabilityType.value = true;
    }
    
    // Start adding a new capability (after at least one capability has been added)
    const startAddingCapability = () => {
      selectingCapabilityType.value = false;
      selectedCapabilityType.value = 'MonthlyCreditsCapability';
      changeCapabilityType('MonthlyCreditsCapability');
    }

    const addCapability = () => {
      form.capabilities.push({ ...currentCapability.value });
      hasAddedFirstCapability.value = true;

      // Reset selection state
      selectedCapabilityType.value = null;
      selectingCapabilityType.value = true;

      // Reset current capability
      currentCapability.value = {
        type: 'MonthlyCreditsCapability',
        description: '',
        credits: 0,
        promotionType: 'volume'
      }
    }

    const removeCapability = (index: number) => {
      form.capabilities.splice(index, 1)
      if (form.capabilities.length === 0) {
        hasAddedFirstCapability.value = false;
      }
    }

    const submit = async () => {
      // Don't allow submission if no capabilities added
      if (form.capabilities.length === 0) {
        return;
      }

      try {
        submitting.value = true
        const period: SubscriptionPeriod = form.periodType === 'TimeLimitedSubscriptionPeriod' ? {
          type: 'TimeLimitedSubscriptionPeriod',
          startDate: form.startDate,
          endDate: form.endDate
        } : {
          type: 'InfiniteSubscriptionPeriod',
          startDate: form.startDate,
        }

        const payload: AddSubscriptionRequest = {
          name: form.name,
          price: form.price,
          capabilities: form.capabilities,
          period,
          invoiceCreationPeriod: form.invoiceCreationPeriod,
          enableCostCenterInput: form.enableCostCenterInput,
        }

        await addCompanySubscription(props.companyId, payload)
        context.emit('subscription-added')
        close()
      } catch (error) {
        console.error('Failed to add subscription:', error)
      } finally {
        submitting.value = false
      }
    }

    return {
      form,
      currentCapability,
      submitting,
      formatPrice,
      getPromotionTitle: (promotionType: string) => {
        return getPromotionTitle(promotionType, i18n.t.bind(i18n), props.company?.volumePackageIsLight);
      },
      close,
      submit,
      addCapability,
      removeCapability,
      allPromotions,
      PaymentTypes,
      changeCapabilityType,
      selectingCapabilityType,
      selectedCapabilityType,
      selectCapabilityType,
      cancelCapabilitySelection,
      startAddingCapability,
      isFormValid,
      isCurrentCapabilityValid,
      hasAddedFirstCapability,
      InvoiceCreationPeriod
    }
  }
});